import {EventEmitter} from "nate-react-api-helpers";
import {ImperialDistance} from "../../../../../misc/ImperialDistance";
import {PrepsEnriched} from "../../../../../api/Products";

export type PrepValues = {
    a: PrepValue;
    b: PrepValue;
    c: PrepValue;
    d: PrepValue;
    e: PrepValue;
    aStar: PrepValue;
    dbf: PrepValue;
    dbfStar: PrepValue;
    clf: PrepValue;
    clfStar: PrepValue;
    dbd: PrepValue;
    dbdStar: PrepValue;
    cld: PrepValue;
    cldStar: PrepValue;
    bs: PrepValue;
}

type PrepValue = {
    value: number;
    enabled: boolean;
}

export type InternalValue = {
    cValue: number; // Calculated/default value
    actualValue: number; // Actual value
    value?: number; // Set value
    enabled: boolean; // Show this value
    changed: EventEmitter<true>;
    label: string; // Label (calculated here)
    displayAsOverride?: boolean;
}

export type PrepInternalOption = {
    name: string;
    value: InternalValue;
    onChange: (v: number | undefined, enabled: boolean) => void;
}

type InternalValues = {
    a: InternalValue;
    b: InternalValue;
    c: InternalValue;
    d: InternalValue;
    e: InternalValue;
    aStar: InternalValue;
    dbf: InternalValue;
    dbfStar: InternalValue;
    clf: InternalValue;
    clfStar: InternalValue;
    dbd: InternalValue;
    dbdStar: InternalValue;
    cld: InternalValue;
    cldStar: InternalValue;
    bs: InternalValue;
}

export class PrepCanvasManager {
    values: InternalValues;

    hideExactDimensions: boolean = false;

    nominalHeight = 0;
    nominalWidth: number = 0;
    secondaryWidth: number = 0;
    overrideHingeQty: number = 0;
    hingeHeight: number = 0;
    hasDeadbolt: boolean;
    overCutDoor: number = 0;
    underCutDoor: number = 0;
    underCutFrame: number = 0;

    pair: boolean = false;
    afterCalculate = new EventEmitter<true>();

    constructor(input: {
        nominalHeight: number,
        nominalWidth: number,
        secondaryWidth?: number,
        overrides: Partial<PrepValues> | undefined,
        pair: boolean
        hingeHeight: number,
        overrideHingeQty: number | 0;
        hasDeadbolt: boolean;
        overCutDoor: number | 0;
        underCutDoor: number | 0;
        underCutFrame: number | 0;
    }) {
        const {nominalHeight, nominalWidth, secondaryWidth, overrides, pair, overrideHingeQty, hingeHeight} = input;
        this.hasDeadbolt = input.hasDeadbolt;
        this.nominalHeight = nominalHeight;
        this.nominalWidth = nominalWidth;
        this.secondaryWidth = secondaryWidth || 0;
        this.pair = pair;
        this.overrideHingeQty = overrideHingeQty;
        this.hingeHeight = hingeHeight;
        this.overCutDoor = input.overCutDoor;
        this.underCutDoor = input.underCutDoor;
        this.underCutFrame = input.underCutFrame;

        console.log("prepCanvasManager", {
            nominalHeight: ImperialDistance.fromUniversal(input.nominalHeight).toString(),
            nominalWidth: ImperialDistance.fromUniversal(input.nominalWidth).toString(),
            secondaryWidth: ImperialDistance.fromUniversal(input.secondaryWidth || 0).toString(),
            overrides: overrides,
            pair: pair,
            overrideHingeQty: overrideHingeQty,
            hingeHeight: hingeHeight,
        });

        this.values = {
            a: this.makeValue(overrides?.a, "A"),
            b: this.makeValue(overrides?.b, "B"),
            c: this.makeValue(overrides?.c, "C"),
            d: this.makeValue(overrides?.d, "D"),
            e: this.makeValue(overrides?.e, "E"),
            aStar: this.makeValue(overrides?.aStar, "A*"),
            dbf: this.makeValue(overrides?.dbf, "DBF"),
            dbfStar: this.makeValue(overrides?.dbfStar, "DBF*"),
            clf: this.makeValue(overrides?.clf, "CLF"),
            clfStar: this.makeValue(overrides?.clfStar, "CLF*"),
            dbd: this.makeValue(overrides?.dbd, "DBD"),
            dbdStar: this.makeValue(overrides?.dbdStar, "DBD*"),
            cld: this.makeValue(overrides?.cld, "CLD"),
            cldStar: this.makeValue(overrides?.cldStar, "CLD*"),
            bs: this.makeValue(overrides?.bs, "BS"),
        }

        if(!overrides?.bs) {
            this.values.bs.enabled = false;
        }

        if(!input.hasDeadbolt) {
            if (!overrides?.dbf && !overrides?.dbfStar && !overrides?.dbd && !overrides?.dbdStar) {
                this.values.dbf.enabled = false;
                this.values.dbfStar.enabled = false;
                this.values.dbd.enabled = false;
                this.values.dbdStar.enabled = false;
            }
        }

        this.calculate();
    }

    clearOverride(k: keyof InternalValues) {
        const v = this.values[k];
        if(v.value !== undefined) {
            v.value = undefined;
            v.changed.emit(true);
        }
    }

    getHingeQty() {
        if(this.overrideHingeQty > 0) {
            return this.overrideHingeQty;
        }

        if(this.values.e.enabled) {
            return 5;
        }

        if(this.values.d.enabled) {
            return 4;
        }

        return 3;
    }

    makeValue(input: PrepValue | undefined, name: string) {
        return {
            cValue: 0,
            label: name,
            value: input?.value || undefined,
            actualValue: ifUndef(input?.value, 0),
            enabled: input?.enabled ?? true,
            changed: new EventEmitter<true>(),
        }
    }

    setEnabled(key: keyof InternalValues, enabled: boolean) {
        const v = this.values[key];
        if(v.enabled === enabled) return;

        v.enabled = enabled;
        v.changed.emit(true);
    }

    calculate() {
        this.values.a.cValue = universal("9.75\"") - this.hingeHeight/2;
        this.values.aStar.cValue = universal("9.625\"") - this.hingeHeight/2 - this.overCutDoor;

        for(let i of Object.keys(this.values)) {
            this.values[i].displayAsOverride = this.isOverride(i as any);
        }

        const changes: InternalValue[] = [
            this.values.a,
            this.values.aStar
        ];

        function setCValue(v: InternalValue, newValue: number) {
            if(v.cValue === newValue) return;

            v.cValue = newValue;
            if(changes.indexOf(v) === -1) changes.push(v);
        }

        function setDspOverride(v: InternalValue) {
            v.displayAsOverride = true;
        }

        // see also preploc/main.go LocationSet.calculate()

        if(this.isOverride("a")) {
            setCValue(this.values.aStar, this.actualValue("a") - universal("1/8") - this.overCutDoor)
            setDspOverride(this.values.aStar);
        } else if(this.isOverride("aStar")) {
            setCValue(this.values.a, this.actualValue("aStar") + universal("1/8") + this.overCutDoor)
            setDspOverride(this.values.a);
        }

        const height = this.lookupHingeSpacing(this.nominalHeight);

        setCValue(this.values.b, height.default.hingeSpacing);
        setCValue(this.values.c, height.default.hingeSpacing);
        setCValue(this.values.d, height.default.hingeSpacing);
        setCValue(this.values.e, height.default.hingeSpacing);

        if(this.overrideHingeQty !== 0) {
            this.setEnabled("c", this.overrideHingeQty >= 3);
            this.setEnabled("d", this.overrideHingeQty >= 4);
            this.setEnabled("e", this.overrideHingeQty >= 5);
        } else {

            const abcd = this.actualValue("a") + this.actualValue("b") + this.actualValue("c") + this.actualValue("d");
            if (abcd < this.nominalHeight) {
                this.setEnabled("d", true);
            } else {
                this.setEnabled("d", false);
            }

            const abcde = abcd + this.actualValue("e")
            if (abcde < this.nominalHeight) {
                this.setEnabled("e", true)
            } else {
                this.setEnabled("e", false)
            }
        }

        if(height.override) {
            if(this.values.b.value === undefined) this.values.b.value = height.override.hingeSpacing;
            if(this.values.c.value === undefined && this.values.c.enabled) this.values.c.value = height.override.hingeSpacing;
            if(this.values.d.value === undefined && this.values.d.enabled) this.values.d.value = height.override.hingeSpacing;
            if(this.values.e.value === undefined && this.values.e.enabled) this.values.e.value = height.override.hingeSpacing;
        }

        setCValue(this.values.clfStar, this.nominalHeight - universal("40 5/16\""));
        setCValue(this.values.clf, this.innerFrameHeight() - this.actualValue("clfStar"));

        if(this.isOverride("clfStar")) {
            setCValue(this.values.clf, this.innerFrameHeight() - this.actualValue("clfStar"));
            setDspOverride(this.values.clf);
        } else if(this.isOverride("clf")) {
            setCValue(this.values.clfStar, this.innerFrameHeight() - this.actualValue("clf"));
            setDspOverride(this.values.clfStar);
        }

        setCValue(this.values.cldStar, this.nominalHeight - universal("7/8\"") - this.overCutDoor - universal("39 9/16\""))
        setCValue(this.values.cld, this.exactDoorHeight() - this.actualValue("cldStar"));

        if(this.isOverride("cldStar")) {
            setCValue(this.values.cld, this.exactDoorHeight() - this.actualValue("cldStar"));
            setDspOverride(this.values.cld);
        } else if(this.isOverride("cld")) {
            setCValue(this.values.cldStar, this.exactDoorHeight() - this.actualValue("cld"));
            setDspOverride(this.values.cldStar);
        }

        // link clf and cld
        const hasClf = this.isOverride("clfStar") || this.isOverride("clf")
        const hasCld = this.isOverride("cldStar") || this.isOverride("cld")
        if(hasCld !== hasClf) {
            if(hasCld) {
                setCValue(this.values.clf, this.actualValue("cld") + this.underCutDoor + universal("3/4\"") - this.underCutFrame);
                setCValue(this.values.clfStar, this.innerFrameHeight() - this.actualValue("clf"));
                setDspOverride(this.values.clf);
                setDspOverride(this.values.clfStar);
            } else {
                setCValue(this.values.cld, this.actualValue("clf") + this.underCutFrame - universal("3/4\"") - this.underCutDoor);
                setCValue(this.values.cldStar, this.exactDoorHeight() - this.actualValue("cld"));
                setDspOverride(this.values.cld);
                setDspOverride(this.values.cldStar);
            }
        }

        setCValue(this.values.dbfStar, this.nominalHeight - universal("48\""));
        setCValue(this.values.dbf, this.innerFrameHeight() - this.actualValue("dbfStar"));

        if(this.isOverride("dbfStar")) {
            setCValue(this.values.dbf, this.innerFrameHeight() - this.actualValue("dbfStar"));
            setDspOverride(this.values.dbf);
        } else if(this.isOverride("dbf")) {
            setCValue(this.values.dbfStar, this.innerFrameHeight() - this.actualValue("dbf"));
            setDspOverride(this.values.dbfStar);
        }

        setCValue(this.values.dbdStar, this.nominalHeight - universal("7/8\"") - this.overCutDoor - universal("47 1/4\""));
        setCValue(this.values.dbd, this.exactDoorHeight() - this.actualValue("dbdStar"));

        if(this.isOverride("dbdStar")) {
            setCValue(this.values.dbd, this.exactDoorHeight() - this.actualValue("dbdStar"));
            setDspOverride(this.values.dbd);
        } else if(this.isOverride("dbd")) {
            setCValue(this.values.dbdStar, this.exactDoorHeight() - this.actualValue("dbd"));
            setDspOverride(this.values.dbdStar);
        }

        // link dbf and dbd
        const hasDbf = this.isOverride("dbfStar") || this.isOverride("dbf")
        const hasDbd = this.isOverride("dbdStar") || this.isOverride("dbd")
        if(hasDbd !== hasDbf) {
            if(hasDbd) {
                setCValue(this.values.dbf, this.actualValue("dbd") + this.underCutDoor + universal("3/4\"") - this.underCutFrame);
                setCValue(this.values.dbfStar, this.innerFrameHeight() - this.actualValue("dbf"));
                setDspOverride(this.values.dbf);
                setDspOverride(this.values.dbfStar);
            } else {
                setCValue(this.values.dbd, this.actualValue("dbf") + this.underCutFrame - universal("3/4\"") - this.underCutDoor);
                setCValue(this.values.dbdStar, this.exactDoorHeight() - this.actualValue("dbd"));
                setDspOverride(this.values.dbd);
                setDspOverride(this.values.dbdStar);
            }
        }

        setCValue(this.values.bs, universal("2 3/4\""));

        // If the value is set to the calculated value, then set it to undefined
        for(let i in this.values) {
            // @ts-ignore
            const val = this.values[i];

            if(val.value && val.cValue === val.value) {
                val.value = undefined;
                val.changed.emit(true);
            }

            val.actualValue = ifUndef(val.value, val.cValue);
        }

        this.afterCalculate.emit(true);
        changes.map(v => v.changed.emit(true))
    }

    isOverride(key: keyof InternalValues) {
        const v = this.values[key]
        return (v.value !== undefined)
    }

    actualValue(key: keyof InternalValues) {
        const v = this.values[key];
        return ifUndef(v.value, v.cValue);
    }

    options() {
        // order is important for backend calculations
        const list = [
            this.makeOption("a"),
            this.makeOption("aStar"),
            this.makeOption("b"),
            this.makeOption("c"),
            this.makeOption("d"),
            this.makeOption("e"),
            !this.pair && this.makeOption("dbf", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.dbfStar.value = undefined;
                }

                this.values.dbfStar.enabled = enabled;
                this.values.dbfStar.changed.emit(true);
            }),
            !this.pair && this.makeOption("dbfStar", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.dbf.value = undefined;
                }

                this.values.dbf.enabled = enabled;
                this.values.dbf.changed.emit(true);
            }),
            !this.pair && this.makeOption("clf", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.clfStar.value = undefined;
                }

                this.values.clfStar.enabled = enabled;
                this.values.clfStar.changed.emit(true);
            }),
            !this.pair && this.makeOption("clfStar", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.clf.value = undefined;
                }

                this.values.clf.enabled = enabled;
                this.values.clf.changed.emit(true);
            }),
            this.makeOption("cld", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.cldStar.value = undefined;
                }

                this.values.cldStar.enabled = enabled;
                this.values.cldStar.changed.emit(true);
            }),
            this.makeOption("cldStar", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.cld.value = undefined;
                }

                this.values.cld.enabled = enabled;
                this.values.cld.changed.emit(true);
            }),

            this.makeOption("dbd", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.dbdStar.value = undefined;
                }

                this.values.dbdStar.enabled = enabled;
                this.values.dbdStar.changed.emit(true);
            }),
            this.makeOption("dbdStar", (v: number | undefined, enabled: boolean) => {
                if(v !== undefined) {
                    this.values.dbd.value = undefined;
                }

                this.values.dbd.enabled = enabled;
                this.values.dbd.changed.emit(true);
            }),
            this.makeOption("bs"),
        ]

        return list.filter(v => !!v) as PrepInternalOption[];
    }

    makeOption(name: keyof PrepValues, onChange?: (v: number | undefined, enabled: boolean) => void): PrepInternalOption {
        return {
            name: name,
            value: this.values[name],
            onChange: (v: number | undefined, enabled: boolean) => {
                // @ts-ignore
                const val = this.values[name];

                if(v === 0) v = undefined;

                val.value = v;
                val.enabled = enabled;

                if(onChange) {
                    onChange(v, enabled);
                }

                this.calculate();
                val.changed.emit(true);
            }}
    }

    exactDoorHeight() {
        return this.nominalHeight - universal("7/8\"") - this.overCutDoor - this.underCutDoor;
    }

    innerFrameHeight() {
        return this.nominalHeight - this.underCutFrame
    }

    calculateHingeSpacingForOverride(nominalHeight: number, qty: number) {
        const actual = nominalHeight - universal("7/8\"");
        const usable = actual - universal("14 3/4\"")
        const hingeSize = this.hingeHeight;
        const usableLessHinge = usable - hingeSize;
        const hingeSpacing = usableLessHinge / (qty - 1);
        const roundedTo64th = Math.round(hingeSpacing * 64) / 64;

        return {
            nominalHeight: nominalHeight,
            exactHeight: usable,
            hingeSpacing: roundedTo64th,
            hingeCount: qty,
        }
    }

    lookupHingeSpacing(nominalHeight: number) {
        let override: any | null = null;
        if(this.overrideHingeQty) {
            override = this.calculateHingeSpacingForOverride(nominalHeight, this.overrideHingeQty)
        }

        const df = getDefaultHingeDetail(nominalHeight)
        return {
            default: df,
            override: override,
        }
    }
}

function getDefaultHingeDetail(nominalHeight: number) {
    for(let i = 0; i < heights.length-1; i++) {
        if(heights[i+1].nominalHeight > nominalHeight) {
            return heights[i]
        }
    }

    return heights[heights.length - 1]
}

export function getHingeQtyOverride(preps: PrepsEnriched, nominalHeight: string): number | undefined {
    if(!preps.customOffsets || preps.customOffsets.type !== "customOffsets") return undefined;
    const off = preps.customOffsets.customOffsets
    const checkNames = [
        "a", "b", "c", "d", "e", "f",
    ];

    const hingeOffsets = off.filter(v => checkNames.indexOf(v.name) !== -1 && v.enabled)
    const offsetCount = hingeOffsets.length;

    const expectCount = getDefaultHingeDetail(universal(nominalHeight)).hingeCount;
    if(expectCount === offsetCount) {
        return undefined;
    }

    return offsetCount;
}

// see also MdHinges.ts
const heights: {nominalHeight: number, exactHeight: number, hingeSpacing: number, hingeCount: number}[] = [
    {nominalHeight: universal("5'2"), exactHeight: universal("61 1/8"), hingeSpacing: universal("20 15/16"), hingeCount: 3},
    {nominalHeight: universal("5'4"), exactHeight: universal("63 1/8"), hingeSpacing: universal("21 15/16"), hingeCount: 3},
    {nominalHeight: universal("5'6"), exactHeight: universal("65 1/8"), hingeSpacing: universal("22 15/16"), hingeCount: 3},
    {nominalHeight: universal("5'8"), exactHeight: universal("67 1/8"), hingeSpacing: universal("23 15/16"), hingeCount: 3},
    {nominalHeight: universal("5'10"), exactHeight: universal("69 1/8"), hingeSpacing: universal("24 15/16"), hingeCount: 3},
    {nominalHeight: universal("6'0"), exactHeight: universal("71 1/8"), hingeSpacing: universal("25 15/16"), hingeCount: 3},
    {nominalHeight: universal("6'2"), exactHeight: universal("73 1/8"), hingeSpacing: universal("26 15/16"), hingeCount: 3},
    {nominalHeight: universal("6'4"), exactHeight: universal("75 1/8"), hingeSpacing: universal("27 15/16"), hingeCount: 3},
    {nominalHeight: universal("6'6"), exactHeight: universal("77 1/8"), hingeSpacing: universal("28 15/16"), hingeCount: 3},
    {nominalHeight: universal("6'8"), exactHeight: universal("79 1/8"), hingeSpacing: universal("29 15/16"), hingeCount: 3},
    {nominalHeight: universal("6'10"), exactHeight: universal("81 1/8"), hingeSpacing: universal("30 15/16"), hingeCount: 3},
    {nominalHeight: universal("7'0"), exactHeight: universal("83 1/8"), hingeSpacing: universal("31 15/16"), hingeCount: 3},
    {nominalHeight: universal("7'2"), exactHeight: universal("85 1/8"), hingeSpacing: universal("32 15/16"), hingeCount: 3},
    {nominalHeight: universal("7'4"), exactHeight: universal("87 1/8"), hingeSpacing: universal("33 15/16"), hingeCount: 3},
    {nominalHeight: universal("7'6"), exactHeight: universal("89 1/8"), hingeSpacing: universal("34 15/16"), hingeCount: 3},
    {nominalHeight: universal("7'8"), exactHeight: universal("91 1/8"), hingeSpacing: universal("23 61/64"), hingeCount: 4},
    {nominalHeight: universal("7'10"), exactHeight: universal("93 1/8"), hingeSpacing: universal("24 5/8"), hingeCount: 4},
    {nominalHeight: universal("8'0"), exactHeight: universal("95 1/8"), hingeSpacing: universal("25 19/64"), hingeCount: 4},
    {nominalHeight: universal("8'2"), exactHeight: universal("97 1/8"), hingeSpacing: universal("25 61/64"), hingeCount: 4},
    {nominalHeight: universal("8'4"), exactHeight: universal("99 1/8"), hingeSpacing: universal("26 5/8"), hingeCount: 4},
    {nominalHeight: universal("8'6"), exactHeight: universal("101 1/8"), hingeSpacing: universal("27 19/64"), hingeCount: 4},
    {nominalHeight: universal("8'8"), exactHeight: universal("103 1/8"), hingeSpacing: universal("27 61/64"), hingeCount: 4},
    {nominalHeight: universal("8'10"), exactHeight: universal("105 1/8"), hingeSpacing: universal("28 5/8"), hingeCount: 4},
    {nominalHeight: universal("9'0"), exactHeight: universal("107 1/8"), hingeSpacing: universal("29 19/64"), hingeCount: 4},
    {nominalHeight: universal("9'2"), exactHeight: universal("109 1/8"), hingeSpacing: universal("29 61/64"), hingeCount: 4},
    {nominalHeight: universal("9'4"), exactHeight: universal("111 1/8"), hingeSpacing: universal("30 5/8"), hingeCount: 4},
    {nominalHeight: universal("9'6"), exactHeight: universal("113 1/8"), hingeSpacing: universal("31 19/64"), hingeCount: 4},
    {nominalHeight: universal("9'8"), exactHeight: universal("115 1/8"), hingeSpacing: universal("31 61/64"), hingeCount: 4},
    {nominalHeight: universal("9'10"), exactHeight: universal("117 1/8"), hingeSpacing: universal("32 5/8"), hingeCount: 4},
    {nominalHeight: universal("10'0"), exactHeight: universal("119 1/8"), hingeSpacing: universal("33 19/64"), hingeCount: 4},
    {nominalHeight: universal("10'2"), exactHeight: universal("121 1/8"), hingeSpacing: universal("25 15/32"), hingeCount: 5},
    {nominalHeight: universal("10'4"), exactHeight: universal("123 1/8"), hingeSpacing: universal("25 31/32"), hingeCount: 5},
    {nominalHeight: universal("10'6"), exactHeight: universal("125 1/8"), hingeSpacing: universal("26 15/32"), hingeCount: 5},
    {nominalHeight: universal("10'8"), exactHeight: universal("127 1/8"), hingeSpacing: universal("26 31/32"), hingeCount: 5},
    {nominalHeight: universal("10'10"), exactHeight: universal("129 1/8"), hingeSpacing: universal("27 15/32"), hingeCount: 5},
    {nominalHeight: universal("11'0"), exactHeight: universal("131 1/8"), hingeSpacing: universal("27 31/32"), hingeCount: 5},
]

function ifUndef(a: number | undefined, b: number) {
    if(a === undefined) return b;
    return a;
}

function universal(value: string) {
    return ImperialDistance.parse(value).universalValue()
}